import * as React from 'react'
import { useCallback, useMemo } from 'react'

import type {
  IProgram,
  IProgramInteraction,
  IProgramInteractionId,
} from '@app/domain/program/program.interface'
import {
  IInteractionStatusId,
  ProgramInteractionTypeId,
} from '@app/domain/program/program.interface'
import { theme } from '@app/utils/theme'

import {
  InteractionLabel,
  StyledSelectGroup,
  StyledSelectOption,
} from './styled-select-group'
import { createDaysRemainingLabel, findNearestInteractionId } from './utils'

const DEFAULT_FOLLOW_UP_INTERACTION_LABEL = 'Follow-up'
const FOLLOW_UP_INTERACTION_GROUP_LABEL = 'Follow-ups' //this one

export type props = {
  initialInteractionCompletedDate: Date
  program: IProgram
  programInteractionHistory: Record<IProgramInteractionId, Date>
  isDisabled?: boolean
}

export const FollowUpInteractionSelectOptionsComponent = ({
  initialInteractionCompletedDate,
  program,
  programInteractionHistory,
  isDisabled,
}: props) => {
  const initialInteractionIsCompleted =
    initialInteractionCompletedDate !== undefined

  const nearestFollowUpInteractionId = useMemo(() => {
    return findNearestInteractionId(
      initialInteractionCompletedDate,
      program,
      programInteractionHistory,
      ProgramInteractionTypeId.FollowUpInteraction,
    )
  }, [initialInteractionCompletedDate, program, programInteractionHistory])

  const getDaysRemainingLabel = useCallback(
    (interaction: IProgramInteraction) => {
      return createDaysRemainingLabel(
        initialInteractionCompletedDate,
        interaction,
        nearestFollowUpInteractionId,
        programInteractionHistory,
      )
    },
    [
      initialInteractionCompletedDate,
      nearestFollowUpInteractionId,
      programInteractionHistory,
    ],
  )

  const followUpInteractionSelect = useCallback(
    (
      interaction: IProgramInteraction,
      initialInteractionIsCompleted: boolean,
      programInteractionHistory: Record<IProgramInteractionId, Date>,
    ) => {
      const isNearest = interaction.id === nearestFollowUpInteractionId
      const interactionIsComplete =
        programInteractionHistory[interaction.id] !== undefined
      const shouldStrikeThrough =
        !initialInteractionIsCompleted || interactionIsComplete

      if (
        interaction.interactionType ===
          ProgramInteractionTypeId.FollowUpInteraction &&
        (interaction.status === IInteractionStatusId.Active || isDisabled)
      ) {
        const interactionIsComplete =
          programInteractionHistory[interaction.id] !== undefined

        return (
          <StyledSelectOption
            key={interaction.id}
            value={interaction.id}
            description={
              !initialInteractionIsCompleted
                ? 'Disabled pending initial interaction.'
                : getDaysRemainingLabel(interaction)
            }
            isDisabled={!initialInteractionIsCompleted || interactionIsComplete}
            $isNearest={isNearest}
            $activeColor={isNearest ? theme.colors.active : undefined}
          >
            <InteractionLabel $strikeThrough={shouldStrikeThrough}>
              {interaction.label?.length > 0
                ? interaction.label
                : DEFAULT_FOLLOW_UP_INTERACTION_LABEL}
            </InteractionLabel>
          </StyledSelectOption>
        )
      } else {
        return null
      }
    },
    [getDaysRemainingLabel, isDisabled, nearestFollowUpInteractionId],
  )

  if (program.interactions[ProgramInteractionTypeId.FollowUpInteraction]) {
    return (
      <StyledSelectGroup label={FOLLOW_UP_INTERACTION_GROUP_LABEL}>
        {program.interactions[ProgramInteractionTypeId.FollowUpInteraction]
          .sort(
            (a, b) =>
              a.daysAfterInitialInteraction - b.daysAfterInitialInteraction,
          )
          .map((interaction) =>
            followUpInteractionSelect(
              interaction,
              initialInteractionIsCompleted,
              programInteractionHistory,
            ),
          )}
      </StyledSelectGroup>
    )
  } else {
    return null
  }
}
